import { t } from "@/lib/i18n/interpolate";
import {
  conceptsToQuery,
  genericQuery,
} from "@/lib/tool-kit/research-workflow/boolean";

/**
 * Clinical questions from PICO elements.
 *
 * PICO — Population, Intervention (or exposure), Comparison, Outcome — is the anatomy
 * of a "well-built clinical question" set out by Richardson, Wilson, Nishikawa and
 * Hayward in 1995. Adding a Time frame makes PICOT; adding the Study design makes
 * PICOS. The same four or five elements are phrased differently for each kind of
 * question, because the elements play different roles: in a diagnosis question the "I"
 * is the test being judged and the "C" its reference standard; in an aetiology question
 * the "I" is an exposure, not something anyone chose.
 *
 * Every phrasing is a template in the widget's strings, filled with the reader's own
 * words; a missing element is shown as its bracketed name.
 */

export type PicoType =
  | "therapy"
  | "prevention"
  | "diagnosis"
  | "prognosis"
  | "aetiology"
  | "meaning";

export const PICO_TYPES: readonly PicoType[] = [
  "therapy",
  "prevention",
  "diagnosis",
  "prognosis",
  "aetiology",
  "meaning",
];

export type PicoElement = "p" | "i" | "c" | "o" | "t" | "s";

export const PICO_ELEMENTS: readonly PicoElement[] = [
  "p",
  "i",
  "c",
  "o",
  "t",
  "s",
];

export interface PicoInput {
  /** The element as it should read in the question: "older adults living at home". */
  values: Record<PicoElement, string>;
  /** Search terms for each element — keywords and synonyms, one per line. */
  synonyms: Record<PicoElement, string>;
  /** Whether the element joins the search string. */
  include: Record<PicoElement, boolean>;
}

export const EMPTY_PICO_INPUT: PicoInput = {
  values: { p: "", i: "", c: "", o: "", t: "", s: "" },
  synonyms: { p: "", i: "", c: "", o: "", t: "", s: "" },
  // Comparison, time and design usually narrow a first search too far.
  include: { p: true, i: true, c: false, o: true, t: false, s: false },
};

export interface PicoPhrases {
  withComparison: Readonly<Record<PicoType, string>>;
  withoutComparison: Readonly<Record<PicoType, string>>;
  timeClause: Readonly<Record<PicoType, string>>;
  missing: Readonly<Record<PicoElement, string>>;
}

function value(input: PicoInput, element: PicoElement): string {
  return input.values[element].trim();
}

/** PICO, PICOT, PICOS or PICOTS, from the elements that have been filled in. */
export function frameworkName(input: PicoInput): string {
  return `PICO${value(input, "t") ? "T" : ""}${value(input, "s") ? "S" : ""}`;
}

/** The question for one type, in the reader's words. */
export function picoQuestion(
  input: PicoInput,
  type: PicoType,
  p: PicoPhrases
): string {
  const fill = (element: PicoElement) =>
    value(input, element) || p.missing[element];
  const time = value(input, "t")
    ? t(p.timeClause[type], { t: value(input, "t") })
    : "";
  const template =
    value(input, "c") && type !== "meaning"
      ? p.withComparison[type]
      : p.withoutComparison[type];
  return t(template, {
    p: fill("p"),
    i: fill("i"),
    c: fill("c"),
    o: fill("o"),
    time,
  });
}

/**
 * The search terms for an element: its own search-terms box, or — when that is empty —
 * the element as written. The question's wording ("a home-based exercise programme")
 * is rarely a good search term, so it is only the fallback.
 */
export function elementTerms(input: PicoInput, element: PicoElement): string {
  const own = input.synonyms[element].trim();
  return own === "" ? value(input, element) : own;
}

/** The included elements that have terms, in PICOTS order. */
export function searchElements(input: PicoInput): PicoElement[] {
  return PICO_ELEMENTS.filter(
    (element) => input.include[element] && elementTerms(input, element) !== ""
  );
}

/** `(a OR "b c") AND (d OR e)` across the included elements. */
export function picoSearchString(input: PicoInput): string {
  return genericQuery(
    searchElements(input).map((element) => elementTerms(input, element))
  );
}

/** The query string that opens the Boolean builder with these elements as concepts. */
export function builderQuery(input: PicoInput): string {
  return conceptsToQuery(
    searchElements(input).map((element) => elementTerms(input, element))
  );
}
